import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
dayjs.extend(calendar);

import EventBusInstance from '@core/eventbus';
import { groupBy } from '@utilities/collection-utils';
import { parseDate } from '@utilities/dateTime';
import { generateTemplate } from '@utilities/dom';
import { parseHtml } from '@utilities/parsing';

/*
USAGE:

<div class="d3-plugin" data-plugin="tabbify" data-options="" >
  <script type="text/template" id="js-tabbify-tab-template">
    <pk-tab tab-id="<%= id %>" ><%= dayjs().calendar(dayjs().dayOfYear(id)) %></pk-tab>
  </script>
  <script type="text/template" id="js-tabbify-tab-panel-template">
    <pk-tab-panel slot="content" tab="<%= id %>" >
      <div class="js-tabbify-tab-panel"><div>
    </pk-tab-panel>
  </script>
  <div class="mm-tabs js-tabbify-tab-container"></div>
  <div class="js-tabbify-elem" tab="1" >
    <script type="text/template" id="js-tabbify-tab-content-template">
  </div>
  <div class="js-tabbify-elem" tab="1" >
    <script type="text/template" id="js-tabbify-tab-content-template">
  </div>
  <div class="js-tabbify-elem" tab="2" >
    <script type="text/template" id="js-tabbify-tab-content-template">
  </div>
  <div class="js-tabbify-elem" tab="3" >
    <script type="text/template" id="js-tabbify-tab-content-template">
  </div>
</div>

*/

export type TabbifyPluginOptions = {
  todayDates?: boolean;
  defaultTab?: string;
  dateFormat?: {
    sameDay?: string;
    nextDay?: string;
    nextWeek?: string;
    lastDay?: string;
    lastWeek?: string;
    sameElse?: string;
  };
  dateFormatAbbr?: {
    sameDay?: string;
    nextDay?: string;
    nextWeek?: string;
    lastDay?: string;
    lastWeek?: string;
    sameElse?: string;
  };
  language?: string;
  reording?: {
    onEvent: string;
    dataAttribute: string;
    selector: string;
    map: {
      [key: string]: number;
    };
    keepMatchDate?: boolean;
  };
};

const defaults: TabbifyPluginOptions = {
  todayDates: false,
  defaultTab: '',
  dateFormat: {
    sameDay: '[Today], D MMMM',
    nextDay: '[Tomorrow], D MMMM',
    nextWeek: 'D MMMM',
    lastDay: '[Yesterday], D MMMM',
    lastWeek: 'D MMMM',
    sameElse: 'D MMMM',
  },
  dateFormatAbbr: {
    sameDay: '[Today], D MMM',
    nextDay: '[Tomorrow], D MMM',
    nextWeek: 'D MMM',
    lastDay: '[Yesterday], D MMM',
    lastWeek: 'D MMM',
    sameElse: 'D MMM',
  },
  language: 'en',
  reording: {
    onEvent: null,
    dataAttribute: null,
    selector: null,
    map: {
      LIVE: 0,
      UPCOMING: 1,
      DEFAULT: 2,
    },
    keepMatchDate: false,
  },
};

export default class TabbifyPlugin {
  opt: TabbifyPluginOptions;
  dom: HTMLElement;

  tabContainer: HTMLElement;
  tabTemplate: ReturnType<typeof generateTemplate>;
  panelTemplate: ReturnType<typeof generateTemplate>;

  constructor(dom: HTMLElement, o: TabbifyPluginOptions) {
    this.opt = {
      ...defaults,
      ...o,
      dateFormat: {
        ...defaults.dateFormat,
        ...o?.dateFormat,
      },
      dateFormatAbbr: {
        ...defaults.dateFormatAbbr,
        ...o?.dateFormatAbbr,
      },
      reording: {
        ...defaults.reording,
        ...o?.reording,
        map: {
          ...defaults.reording.map,
          ...o?.reording?.map,
        },
      },
    };
    this.dom = dom;
  }

  get tabs() {
    return Array.from(this.tabContainer.querySelectorAll<HTMLPkTabElement>('pk-tab'));
  }

  get panels() {
    return Array.from(this.dom.querySelectorAll<HTMLPkTabPanelElement>('pk-tab-panel'));
  }

  getElements(container = this.dom) {
    return Array.from(container.querySelectorAll<HTMLElement>('.js-tabbify-elem'));
  }

  init() {
    this.tabTemplate = generateTemplate(
      this.dom.querySelector<HTMLScriptElement>('.js-tabbify-tab-template').innerHTML,
    );
    this.panelTemplate = generateTemplate(
      this.dom.querySelector<HTMLScriptElement>('.js-tabbify-tab-panel-template').innerHTML,
    );
    this.tabContainer = this.dom.querySelector<HTMLPkTabsElement>('.js-tabbify-tab-container');

    this.groupElements();

    this.tabs.forEach((tab) => {
      tab.addEventListener('pkTabButtonClick', (e: CustomEvent<{ tabId: string }>) => {
        this.selectTab(e.detail.tabId);
      });
    });

    const def = this.opt.todayDates ? this.recalcDate(this.opt.defaultTab) : this.opt.defaultTab;
    if (this.opt.defaultTab) this.selectTab(def);

    if (this.opt.reording.onEvent && this.opt.reording.dataAttribute) {
      EventBusInstance.on(this.opt.reording.onEvent, this.reorderElements, this);
    }
  }

  detach() {
    EventBusInstance.off(this.opt.reording.onEvent, this.reorderElements, this);
    this.tabContainer.innerHTML = '';
  }

  /* ------------------------- */
  //Private Functions
  groupElements() {
    const tabs = document.createElement('pk-tabs');
    const groups = groupBy(this.getElements(), (elem) => {
      let key = elem.dataset.tab;
      key = this.opt.todayDates ? this.recalcDate(key) : key;
      elem.dataset.tab = key;
      return key;
    });

    for (const key in groups) {
      const tab = parseHtml(
        this.tabTemplate({
          id: key,
          label: this.getDateLabel(key),
          labelAbbr: this.getDateLabel(key, true),
        }),
        'text/html',
      );
      tabs.appendChild(tab);

      const panel = parseHtml(
        this.panelTemplate({
          id: key,
        }),
        'text/html',
      );

      groups[key].forEach((el) => {
        const contentTemplate = generateTemplate(
          el.querySelector<HTMLScriptElement>('.js-tabbify-tab-content-template').innerHTML,
        );
        const content = parseHtml(contentTemplate(), 'text/html');
        el.appendChild(content);
        panel.querySelector('.js-tabbify-tab-panel').appendChild(el);
      });

      tabs.appendChild(panel);

      this.tabContainer.appendChild(tabs);
    }
  }

  selectTab(tabId: string) {
    const selectedTab = this.tabs.find((e) => e.tabId === tabId) ?? this.tabs.at(0);
    selectedTab?.setAttribute('selected', '');

    if (this.opt.reording.dataAttribute) this.reorderElements();
  }

  recalcDate(date: string) {
    const mDate = parseDate(date);
    return mDate.format('YYYYMMDD');
  }

  getDateLabel = (key: string, abbr?: boolean) => {
    return dayjs(key).calendar(null, abbr ? this.opt.dateFormatAbbr : this.opt.dateFormat);
  };

  mapOrder(value: string) {
    if (!this.opt.reording.map) return parseInt(value);

    return this.opt.reording.map[value] ?? this.opt.reording.map.DEFAULT;
  }

  getOrderValue = (el: HTMLElement) => {
    const value = el?.querySelector<HTMLElement>(
      `${this.opt.reording.selector ?? ''}[data-${this.opt.reording.dataAttribute}]`,
    )?.dataset[this.opt.reording.dataAttribute];
    return this.mapOrder(value);
  };

  reorderElements = () => {
    this.panels.forEach((panel) => {
      const elements = this.getElements(panel);
      elements.forEach((el) => {
        el.style.setProperty('order', `${this.getOrderValue(el)}`);
      });

      elements
        .toSorted((a, b) => this.getOrderValue(a) - this.getOrderValue(b))
        .forEach((el, i) => {
          el?.classList.toggle('tab-element--last', i === elements.length - 1);
        });
    });
  };
}
